import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import swal from 'sweetalert'

import { CatchError } from 'Exceptions/exceptions'
import { EMPTY_BANK_ROUTER_FILTERS } from 'Main/Constant'
import { errorMessageHandler } from 'Main/Utils'
import BankRoutingService from 'Main/service/BankRoutingService'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import { getRefreshToken } from 'variables/common'
import BankRouteModal from './BankRouteModal'

function BankRoutingManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const bankRoutingService = useMemo(() => new BankRoutingService(), [])
  const [vendors, setVendors] = useState([])
  const [channels, setChannels] = useState([])
  const [optionsError, setOptionsError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const [draftFilters, setDraftFilters] = useState(EMPTY_BANK_ROUTER_FILTERS)
  const [filters, setFilters] = useState(EMPTY_BANK_ROUTER_FILTERS)
  const [modalRoute, setModalRoute] = useState(undefined)
  const [modalOpen, setModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [togglingId, setTogglingId] = useState(null)

  useEffect(() => {
    Promise.all([
      bankRoutingService.getPaymentVendors(),
      bankRoutingService.getChannels()
    ]).then(([vendorResponse, channelResponse]) => {
      setVendors(Array.isArray(vendorResponse?.data) ? vendorResponse.data : [])
      setChannels(Array.isArray(channelResponse?.data) ? channelResponse.data : [])
    }).catch((error) => {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) setOptionsError(errorMessageHandler(error, 'Failed to load bank routing options.'))
    })
  }, [bankRoutingService, navigate, refreshToken])

  const getRoutes = useCallback(({ page, pageSize, search, ...activeFilters }) => {
    return bankRoutingService.getAll({
      page,
      size: pageSize,
      ...Object.fromEntries(Object.entries(activeFilters).filter(([, value]) => value !== '')),
      ...(activeFilters.name ? {} : search ? { name: search } : {})
    })
  }, [bankRoutingService])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const openAdd = () => {
    setModalRoute(undefined)
    setModalOpen(true)
  }

  const openEdit = useCallback(async (id) => {
    try {
      const response = await bankRoutingService.getById(id)
      if (!response?.data?.id) throw new Error('Bank route not found')
      setModalRoute(response.data)
      setModalOpen(true)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) await swal('Error', errorMessageHandler(error, 'Failed to load bank route.'), 'error')
    }
  }, [bankRoutingService, navigate, refreshToken])

  const saveRoute = async (payload) => {
    try {
      setSubmitting(true)
      if (modalRoute?.id) await bankRoutingService.update(modalRoute.id, payload)
      else await bankRoutingService.create(payload)

      setModalOpen(false)
      await swal('Success!', modalRoute?.id ? 'Bank route updated.' : 'Bank route added.', 'success')
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) await swal('Error', errorMessageHandler(error, 'Failed to save bank route.'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const toggleRoute = useCallback(async (route) => {
    try {
      setTogglingId(route.id)
      await bankRoutingService.toggle(route.id, !route.enabled)
      setReloadKey((current) => current + 1)
    } catch (error) {
      const caught = CatchError(error, navigate, refreshToken)
      if (caught !== undefined) await swal('Error', errorMessageHandler(error, 'Failed to change route status.'), 'error')
    } finally {
      setTogglingId(null)
    }
  }, [bankRoutingService, navigate, refreshToken])

  const deleteRoute = useCallback((route) => {
    swal('Delete bank route', `Delete ${route.name}? Payments will no longer use this route.`, 'warning', {
      buttons: {
        cancel: 'Cancel',
        confirm: { text: 'Delete', value: 'delete' }
      },
      dangerMode: true
    }).then(async (value) => {
      if (value !== 'delete') return

      try {
        await bankRoutingService.remove(route.id)
        await swal('Success!', 'Bank route deleted.', 'success')
        setReloadKey((current) => current + 1)
      } catch (error) {
        const caught = CatchError(error, navigate, refreshToken)
        if (caught !== undefined) await swal('Error', errorMessageHandler(error, 'Failed to delete bank route.'), 'error')
      }
    })
  }, [bankRoutingService, navigate, refreshToken])

  const applyFilters = (event) => {
    event.preventDefault()
    setFilters(draftFilters)
  }

  const clearFilters = () => {
    setDraftFilters(EMPTY_BANK_ROUTER_FILTERS)
    setFilters(EMPTY_BANK_ROUTER_FILTERS)
  }

  const setFilter = (field) => (event) => {
    setDraftFilters((current) => ({ ...current, [field]: event.target.value }))
  }

  const vendorMap = useMemo(() => new Map(vendors.map((vendor) => [String(vendor.id), vendor])), [vendors])
  const channelMap = useMemo(() => new Map(channels.map((channel) => [channel.value, channel])), [channels])
  const providerLabel = useCallback((route) => {
    const vendorId = String(route.payment_vendors_id || '')
    if (!vendorId) return 'BNI eCollection'
    const vendor = vendorMap.get(vendorId)
    return vendor ? `${vendor.name} (${vendor.enviroment_type})` : `Unavailable vendor (${vendorId})`
  }, [vendorMap])

  const columns = useMemo(() => [
    {
      header: 'Route',
      accessorKey: 'name',
      cell: ({ row, getValue }) => (
        <><strong>{getValue()}</strong><div className="small text-muted">{row.original.id}</div></>
      )
    },
    {
      header: 'Partner / prefix',
      accessorKey: 'patner_service_id',
      cell: ({ row, getValue }) => (
        <><span>{getValue()}</span><div className="small text-muted">Prefix: {row.original.prefix_cust_no}</div></>
      )
    },
    {
      header: 'Merchant BIN',
      accessorKey: 'merchant_bin',
      cell: ({ getValue }) => <code>{getValue()}</code>
    },
    {
      header: 'Bank channel',
      accessorKey: 'bank_channel',
      cell: ({ getValue }) => (
        <><strong>{getValue()}</strong><div className="small text-muted">{channelMap.get(getValue())?.name || 'Unknown channel'}</div></>
      )
    },
    {
      header: 'Provider',
      accessorKey: 'payment_vendors_id',
      cell: ({ row }) => providerLabel(row.original)
    },
    {
      header: 'Status',
      accessorKey: 'enabled',
      cell: ({ getValue }) => <span className={getValue() ? 'status-success' : 'status-danger'}>{getValue() ? 'Enabled' : 'Disabled'}</span>
    },
    {
      header: 'Actions',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      cell: ({ row }) => {
        const route = row.original
        const toggling = togglingId === route.id
        return (
          <div className="d-flex justify-content-end gap-1">
            <button type="button" className={`btn btn-sm ${route.enabled ? 'btn-warning' : 'btn-success'}`} title={route.enabled ? 'Disable route' : 'Enable route'} disabled={toggling} onClick={() => toggleRoute(route)}>
              <i className={`fa-solid ${toggling ? 'fa-circle-o-notch fa-spin' : 'fa-power-off'}`} />
            </button>
            <button type="button" className="btn btn-sm btn-primary" title="Edit route" onClick={() => openEdit(route.id)}>
              <i className="fa-solid fa-pen-to-square" />
            </button>
            <button type="button" className="btn btn-sm btn-danger" title="Delete route" onClick={() => deleteRoute(route)}>
              <i className="fa-solid fa-trash-can" />
            </button>
          </div>
        )
      }
    }
  ], [channelMap, deleteRoute, openEdit, providerLabel, toggleRoute, togglingId])

  const menu = (
    <>
      {optionsError ? <div className="alert alert-danger" role="alert">{optionsError}</div> : null}
      <div className="text-center mb-3">
        <button type="button" className="btn btn-success" onClick={openAdd} disabled={channels.length === 0}>
          <i className="fa-solid fa-plus me-1" /> Tambah Bank Routing
        </button>
      </div>
      <form className="row g-2 align-items-end" onSubmit={applyFilters}>
        <div className="col-12 col-md-3">
          <label className="form-label" htmlFor="bank-filter-name">Route name</label>
          <input id="bank-filter-name" className="form-control" value={draftFilters.name} onChange={setFilter('name')} />
        </div>
        <div className="col-12 col-md-3">
          <label className="form-label" htmlFor="bank-filter-provider">Provider</label>
          <select id="bank-filter-provider" className="form-select" value={draftFilters.payment_vendors_id} onChange={setFilter('payment_vendors_id')}>
            <option value="">All providers</option>
            <option value="__builtin__">BNI eCollection</option>
            {vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
          </select>
        </div>
        <div className="col-12 col-md-2">
          <label className="form-label" htmlFor="bank-filter-channel">Channel</label>
          <select id="bank-filter-channel" className="form-select" value={draftFilters.bank_channel} onChange={setFilter('bank_channel')}>
            <option value="">All channels</option>
            {channels.map((channel) => <option key={channel.value} value={channel.value}>{channel.name}</option>)}
          </select>
        </div>
        <div className="col-12 col-md-2">
          <label className="form-label" htmlFor="bank-filter-status">Status</label>
          <select id="bank-filter-status" className="form-select" value={draftFilters.enabled} onChange={setFilter('enabled')}>
            <option value="">All status</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </div>
        <div className="col-12 col-md-2 d-flex gap-2">
          <button type="submit" className="btn btn-primary flex-grow-1">Apply filters</button>
          <button type="button" className="btn btn-light text-black" onClick={clearFilters}>Clear</button>
        </div>
      </form>
    </>
  )

  return (
    <div className="page-content">
      <section>
        <CommonListApiPaginated
          header="Static VA Routing Manager"
          subtitle=""
          columns={columns}
          getData={getRoutes}
          query={filters}
          menu={menu}
          reloadKey={reloadKey}
          initialPageSize={25}
          loadingLabel="Loading bank routes..."
          emptyLabel="No bank routes found."
          searchPlaceholder="Cari route..."
          onError={handleListError}
        />
      </section>

      <BankRouteModal
        show={modalOpen}
        route={modalRoute}
        vendors={vendors}
        channels={channels}
        submitting={submitting}
        onClose={() => setModalOpen(false)}
        onSubmit={saveRoute}
      />
    </div>
  )
}

export default BankRoutingManager
